import { EventTypes } from '@dicetechnology/doris-core';
import {
    ConnectedContentOverlay,
    ConnectedPauseAd,
    ConnectedTextTrack,
    Loader,
    OverlayLite,
    usePlayerContext
} from '@dicetechnology/doris-ui';
import React, { useEffect, useRef, useState } from 'react';

import { OVERLAY_ACTIVE_DURATION } from '../constants';

import { AdProgress } from './AdProgress';
import { Cover } from './Cover';
import { PlayerControls } from './PlayerControls';
import { ReadyToCast } from './ReadyToCast';

export const PlayerInterface: React.FC = () => {
    const {
        state: { isPaused, isOverlayActive, hasLoaded, waiting, hasPlayed, hasEnded },
        overlayStates: { activateOverlayTimed },
        player
    } = usePlayerContext();
    const [metadata, setMetadata] = useState(() => player?.getMediaInfo()?.metadata);
    const [hasSource, setHasSource] = useState(false);
    const showLoader = waiting || !hasPlayed;
    const className = showLoader ? 'ds-chromecast--waiting' : '';

    const activateOverlayTimedRef = useRef(activateOverlayTimed);
    activateOverlayTimedRef.current = activateOverlayTimed;

    useEffect(() => {
        if (!player) {
            return;
        }

        const context = player
            .createSubscriptionContext()
            .on(EventTypes.SEEKED, () => {
                const isLive = player.getIsLive();
                const isPaused = player.getIsPaused();

                // For Live events - When live edge progresses to stay in sync
                if (!isLive && !isPaused) {
                    activateOverlayTimedRef.current(OVERLAY_ACTIVE_DURATION);
                }
            })
            .on(EventTypes.LOAD_REQUEST, (_, { mediaInfo }) => {
                setMetadata(mediaInfo.metadata);
            })
            .on(EventTypes.DURATION_CHANGE, (_, { duration }) => {
                setHasSource(duration > 0);
            });

        return () => context.unsubscribe();
    }, [player]);

    if (!hasSource || hasEnded) {
        return <ReadyToCast />;
    }

    return (
        <OverlayLite overlayIsActive={isOverlayActive} loaded={hasLoaded} className={className}>
            <Cover {...metadata} paused={isPaused} waiting={waiting} />
            <Loader waiting={showLoader} />
            <AdProgress />
            <ConnectedContentOverlay dynamicFontSize={true}>
                <ConnectedTextTrack resetFontSize={false} />
            </ConnectedContentOverlay>
            <PlayerControls />
            <ConnectedPauseAd />
        </OverlayLite>
    );
};
